"use client";
import { Description, Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { useLingui } from "@lingui/react/macro";
import { IconButton } from "@offstreetparking/design-system/forms";
import { useScrollLock } from "@offstreetparking/design-system/hooks";
import { CloseIcon } from "@offstreetparking/design-system/icons/CloseIcon";
import { classNames } from "@offstreetparking/design-system/utils";
import { Fragment } from "react";
import { useFlowOverlayContainment } from "../../../host/overlay-containment";
import { ContainedOverlay } from "../ContainedOverlay/ContainedOverlay";
import { useDelayedVisibility } from "./useDelayedVisibility";
import { useDrawerInteraction } from "./useDrawerInteraction";

type DrawerProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onAnimationComplete?: () => void;
	title?: React.ReactNode | string;
	description?: string;
	children: React.ReactNode;
	variant?: "bottom" | "right";
};

export const Drawer = (props: DrawerProps) => {
	const { t } = useLingui();
	const contained = useFlowOverlayContainment();
	if (contained) {
		return (
			<ContainedOverlay
				isOpen={props.isOpen}
				onClose={() => {
					props.onOpenChange(false);
					props.onAnimationComplete?.();
				}}
				title={props.title}
				closeLabel={t`Close`}
			>
				{props.children}
			</ContainedOverlay>
		);
	}
	return <PortaledDrawer {...props} />;
};

const PortaledDrawer = ({
	isOpen,
	onOpenChange,
	onAnimationComplete,
	title = "Drawer",
	description,
	children,
	variant = "bottom",
}: DrawerProps) => {
	const {
		panelRef,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		handleMouseLeave,
	} = useDrawerInteraction(isOpen, onOpenChange);

	useScrollLock({ enabled: isOpen });
	const shouldShow = useDelayedVisibility(isOpen);

	return (
		<Transition show={shouldShow} as={Fragment} afterLeave={onAnimationComplete}>
			<Dialog as="div" className="relative z-50 print:hidden" onClose={() => onOpenChange(false)}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-neutral opacity-50" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-hidden">
					<div
						className={
							variant === "right"
								? "flex h-full items-stretch justify-end"
								: "flex h-full items-end justify-center md:items-stretch md:justify-end"
						}
					>
						<TransitionChild
							as="div"
							className={
								variant === "right" ? "h-full w-full lg:max-w-[33vw]" : "w-full md:h-full md:max-w-md"
							}
							enter="transform transition-transform [transition-duration:500ms] ease-out"
							enterFrom={
								variant === "right"
									? "translate-x-full"
									: "translate-y-full md:translate-y-0 md:translate-x-full"
							}
							enterTo="translate-y-0 translate-x-0"
							leave="transform transition-transform duration-300 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]"
							leaveFrom="translate-y-0 translate-x-0"
							leaveTo={
								variant === "right"
									? "translate-x-full"
									: "translate-y-full md:translate-y-0 md:translate-x-full"
							}
						>
							<DialogPanel
								ref={panelRef}
								className={
									variant === "right"
										? "relative flex h-full w-full flex-col bg-white shadow-xl lg:rounded-l-[10px] lg:border-neutral-faded lg:border-l"
										: "relative flex h-full max-h-[80vh] min-h-[50vh] w-full flex-col rounded-t-[10px] bg-white shadow-xl md:max-h-full md:rounded-t-none md:rounded-l-[10px] md:border-neutral-faded md:border-l"
								}
								onTouchStart={handleTouchStart}
								onTouchMove={handleTouchMove}
								onTouchEnd={handleTouchEnd}
								onMouseDown={handleMouseDown}
								onMouseMove={handleMouseMove}
								onMouseUp={handleMouseUp}
								onMouseLeave={handleMouseLeave}
							>
								{variant === "right" && (
									<div className="shrink-0 p-4 pb-0">
										<IconButton
											icon={CloseIcon}
											aria-label="Close"
											intent="tertiary"
											size="sm"
											onClick={() => onOpenChange(false)}
										/>
									</div>
								)}
								{variant === "bottom" && (
									<div className="shrink-0 cursor-grab pt-4 pb-4 md:hidden">
										<div className="mx-auto h-2 w-[100px] rounded-full bg-divider" />
									</div>
								)}
								<div className={classNames(variant === "bottom" ? "p-4" : "", "flex-1 overflow-auto")}>
									<DialogTitle
										className={classNames(
											variant === "right" ? "p-4" : "",
											variant === "bottom" ? "mb-4" : "",
											"grow text-left font-bold text-foreground",
										)}
									>
										{title}
									</DialogTitle>
									<Description className="text-neutral-faded text-sm">{description}</Description>
									{children}
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};
